import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Map, CalendarDays, Receipt, FlaskConical, Users, Truck, Calculator, Gauge, Grid3X3, ArrowRight, ShieldCheck, Trophy } from "lucide-react";
import { api } from "../lib/api";
import { useSEO } from "../lib/seo";
import { SoftwareCard, SoftwareLogo, RatingStars } from "../components/SoftwareCard";

const iconMap = { Map, CalendarDays, Receipt, FlaskConical, Users, Truck };

const personas = [
  { label: "I'm a solo operator", to: "/software/paythepoolman", hint: "Best value pick" },
  { label: "I run 2–10 trucks", to: "/software/skimmer", hint: "Best overall" },
  { label: "Water chemistry is my brand", to: "/software/pool-brain", hint: "Best for QC & LSI" },
  { label: "I do repairs & installs", to: "/software/housecall-pro", hint: "Best for jobs" },
  { label: "I'm switching software", to: "/guides/pool-software-migration-guide", hint: "Migration guide" },
];

export default function Home() {
  const [software, setSoftware] = useState([]);
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [comparisons, setComparisons] = useState([]);
  const [swBySlug, setSwBySlug] = useState({});

  useSEO({
    title: "Best Pool Service Software Reviews & Comparisons 2026",
    description: "Independent reviews, side-by-side comparisons and free tools that help pool service companies choose the right software for routes, chemistry and billing.",
    canonicalPath: "/",
    jsonLd: [
      {
        "@type": "WebSite",
        "@id": `${window.location.origin}/#website`,
        name: "Pool Software Guide",
        url: window.location.origin,
        potentialAction: {
          "@type": "SearchAction",
          target: `${window.location.origin}/search?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@type": "Organization",
        "@id": `${window.location.origin}/#org`,
        name: "Pool Software Guide",
        url: window.location.origin,
        description: "Independent reviews and comparisons of pool service software.",
      },
    ],
  });

  useEffect(() => {
    api.get("/software").then(({ data }) => {
      setSoftware(data.slice(0, 4));
      setSwBySlug(Object.fromEntries(data.map((s) => [s.slug, s])));
    });
    api.get("/categories").then(({ data }) => setCategories(data));
    api.get("/articles").then(({ data }) => setArticles(data.slice(0, 3)));
    api.get("/comparisons").then(({ data }) => setComparisons(data.slice(0, 4)));
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-sky-50 via-white to-background" aria-hidden="true" />
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{ backgroundImage: "url(https://images.pexels.com/photos/1147124/pexels-photo-1147124.jpeg?auto=compress&w=1600)", backgroundSize: "cover", backgroundPosition: "center" }}
          aria-hidden="true"
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="lg:col-span-7">
              <p className="uppercase tracking-[0.2em] text-xs font-semibold text-sky-600 mb-4">8 platforms compared · 2026</p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 leading-none">
                The best pool service software, <span className="text-sky-500">ranked</span> for you
              </h1>
              <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl">
                You searched — here's the answer. We research every major platform on routes, chemistry and billing, and rank them by who they're actually right for. Real pricing, ratings cited from Capterra, no fluff.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/best-pool-service-software" data-testid="hero-bestof-link" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 active:scale-95 transition-colors duration-200">
                  See the full 2026 ranking <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Link>
                <Link to="/comparisons" data-testid="hero-comparisons-link" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-300 bg-white/80 text-slate-700 text-sm font-semibold hover:border-sky-400 hover:text-sky-600 active:scale-95 transition-colors duration-200">
                  Head-to-head comparisons
                </Link>
              </div>

              {/* Persona quick chooser */}
              <div className="mt-10">
                <p className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-400 mb-3">Skip the research — which one describes you?</p>
                <div className="flex flex-wrap gap-2">
                  {personas.map((p) => (
                    <Link key={p.label} to={p.to} data-testid={`persona-chip-${p.to.split("/").pop()}`}
                      className="group inline-flex items-center gap-2 pl-4 pr-3 py-2 rounded-full bg-white border border-border text-sm font-medium text-slate-700 hover:border-sky-400 hover:text-sky-700 active:scale-95 transition-colors duration-200">
                      {p.label}
                      <span className="text-xs text-sky-600 font-semibold hidden sm:inline">→ {p.hint}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Top picks panel — the answer, above the fold */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }} className="lg:col-span-5">
              <div className="bg-white/90 backdrop-blur-xl rounded-2xl border border-border shadow-xl shadow-sky-100/60 p-6" data-testid="hero-top-picks">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading font-bold text-slate-900 text-base flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-amber-500" aria-hidden="true" /> Our top picks — 2026
                  </h2>
                  <Link to="/best-pool-service-software" className="text-xs font-semibold text-sky-600 hover:text-sky-700">Full list</Link>
                </div>
                <div className="space-y-2">
                  {software.slice(0, 3).map((s, i) => (
                    <Link key={s.slug} to={`/software/${s.slug}`} data-testid={`hero-pick-${s.slug}`}
                      className="group flex items-center gap-3 rounded-xl p-3 hover:bg-sky-50 transition-colors duration-200">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center font-heading font-black text-xs shrink-0 ${i === 0 ? "bg-amber-400 text-slate-900" : "bg-slate-900 text-white"}`}>
                        {i + 1}
                      </span>
                      <SoftwareLogo software={s} size="w-10 h-10" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-heading font-bold text-slate-900 group-hover:text-sky-600 transition-colors duration-200">{s.name}</span>
                          <RatingStars rating={s.rating} reviewTotal={s.review_total} />
                        </div>
                        <p className="text-xs text-slate-500 line-clamp-1 mt-0.5">{s.best_for}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-bold text-slate-900">{s.pricing_start}</p>
                        <span className="text-xs font-semibold text-sky-600 inline-flex items-center gap-0.5">Review <ArrowRight className="w-3 h-3" aria-hidden="true" /></span>
                      </div>
                    </Link>
                  ))}
                </div>
                <Link to="/compare/skimmer-vs-pool-brain" data-testid="hero-compare-link"
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600 active:scale-95 transition-colors duration-200">
                  Compare #1 vs #2 side by side
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="uppercase tracking-[0.2em] text-xs font-semibold text-sky-600 mb-2">Browse by need</p>
            <h2 className="font-heading text-lg font-bold text-slate-900">What are you trying to fix?</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((c, i) => {
            const Icon = iconMap[c.icon] || Map;
            return (
              <motion.div key={c.slug} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                <Link to={`/category/${c.slug}`} data-testid={`category-card-${c.slug}`} className="group flex flex-col h-full bg-white rounded-2xl border border-border p-6 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 transition-shadow duration-200">
                  <span className="w-11 h-11 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center mb-4 group-hover:bg-sky-500 group-hover:text-white transition-colors duration-200">
                    <Icon className="w-5 h-5" aria-hidden="true" />
                  </span>
                  <h3 className="font-heading font-bold text-slate-900">{c.name}</h3>
                  <p className="text-sm text-slate-500 mt-2 leading-relaxed">{c.description}</p>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Top rated */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="uppercase tracking-[0.2em] text-xs font-semibold text-sky-600 mb-2">Editor's picks</p>
            <h2 className="font-heading text-lg font-bold text-slate-900">Top-rated pool service software</h2>
          </div>
          <Link to="/software" data-testid="home-all-software-link" className="text-sm font-semibold text-sky-600 hover:text-sky-700 inline-flex items-center gap-1">
            All reviews <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {software.map((s, i) => (
            <SoftwareCard key={s.slug} software={s} rank={i + 1} />
          ))}
        </div>
      </section>

      {/* Head-to-head comparisons */}
      {comparisons.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="uppercase tracking-[0.2em] text-xs font-semibold text-sky-600 mb-2">Head to head</p>
              <h2 className="font-heading text-lg font-bold text-slate-900">Popular comparisons</h2>
            </div>
            <Link to="/comparisons" data-testid="home-all-comparisons-link" className="text-sm font-semibold text-sky-600 hover:text-sky-700 inline-flex items-center gap-1">
              All comparisons <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {comparisons.map((c) => {
              const [a, b] = (c.software_slugs || []).map((s) => swBySlug[s]);
              return (
                <Link key={c.slug} to={`/compare/${c.slug}`} data-testid={`home-compare-${c.slug}`}
                  className="group flex items-center gap-4 bg-white rounded-2xl border border-border p-5 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 transition-shadow duration-200">
                  <div className="flex items-center gap-2 shrink-0">
                    {a && <SoftwareLogo software={a} size="w-11 h-11" />}
                    <span className="font-heading font-black text-slate-300 text-xs">VS</span>
                    {b && <SoftwareLogo software={b} size="w-11 h-11" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-heading font-bold text-slate-900 group-hover:text-sky-600 transition-colors duration-200 truncate">{c.title}</h3>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{c.intro}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-sky-500 shrink-0" aria-hidden="true" />
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Tools bento */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <p className="uppercase tracking-[0.2em] text-xs font-semibold text-sky-600 mb-2">Free tools</p>
        <h2 className="font-heading text-lg font-bold text-slate-900 mb-10">Run your numbers before you buy anything</h2>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <Link to="/tools/route-profitability-calculator" data-testid="home-tool-route" className="md:col-span-7 group bg-slate-900 rounded-2xl p-8 text-white hover:bg-slate-800 transition-colors duration-200">
            <Calculator className="w-8 h-8 text-sky-400 mb-4" aria-hidden="true" />
            <h3 className="font-heading font-bold text-xl">Route Profitability Calculator</h3>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed max-w-md">Model revenue, chemical cost, labor and drive time per route. Find out which routes actually make money.</p>
            <span className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-sky-400">Open calculator <ArrowRight className="w-4 h-4" aria-hidden="true" /></span>
          </Link>
          <Link to="/tools/technician-capacity-estimator" data-testid="home-tool-capacity" className="md:col-span-5 group bg-sky-500 rounded-2xl p-8 text-white hover:bg-sky-600 transition-colors duration-200">
            <Gauge className="w-8 h-8 mb-4" aria-hidden="true" />
            <h3 className="font-heading font-bold text-xl">Technician Capacity Estimator</h3>
            <p className="text-sm text-sky-100 mt-2 leading-relaxed">How many pools can each tech really handle? Know before you hire.</p>
          </Link>
          <Link to="/tools/integration-matrix" data-testid="home-tool-matrix" className="md:col-span-12 group bg-white border border-border rounded-2xl p-8 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 transition-shadow duration-200 flex flex-col md:flex-row md:items-center gap-4">
            <Grid3X3 className="w-8 h-8 text-sky-500 shrink-0" aria-hidden="true" />
            <div className="flex-1">
              <h3 className="font-heading font-bold text-xl text-slate-900">Integration Matrix</h3>
              <p className="text-sm text-slate-500 mt-1">QuickBooks, Stripe, Zapier, SMS — see exactly which platforms connect to the tools you already use.</p>
            </div>
            <span className="inline-flex items-center gap-1 text-sm font-semibold text-sky-600">Compare integrations <ArrowRight className="w-4 h-4" aria-hidden="true" /></span>
          </Link>
        </div>
      </section>

      {/* Guides */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="uppercase tracking-[0.2em] text-xs font-semibold text-sky-600 mb-2">Learn</p>
            <h2 className="font-heading text-lg font-bold text-slate-900">Latest guides</h2>
          </div>
          <Link to="/guides" data-testid="home-all-guides-link" className="text-sm font-semibold text-sky-600 hover:text-sky-700 inline-flex items-center gap-1">
            All guides <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {articles.map((a) => (
            <Link key={a.slug} to={`/guides/${a.slug}`} data-testid={`home-article-${a.slug}`} className="group bg-white rounded-2xl border border-border overflow-hidden hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 transition-shadow duration-200">
              {a.cover_image && <img src={a.cover_image} alt={a.title} className="w-full h-40 object-cover" />}
              <div className="p-6">
                <h3 className="font-heading font-bold text-slate-900 group-hover:text-sky-600 transition-colors duration-200 leading-snug">{a.title}</h3>
                <p className="text-sm text-slate-500 mt-2 line-clamp-3 leading-relaxed">{a.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Trust strip */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="bg-sky-50 rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center gap-6">
          <ShieldCheck className="w-10 h-10 text-sky-500 shrink-0" aria-hidden="true" />
          <div className="flex-1">
            <h3 className="font-heading font-bold text-slate-900 text-lg">How we review software</h3>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">Every review combines hands-on feature evaluation, pricing pulled straight from each vendor's official page, and verified user ratings cited from Capterra. We never accept payment for rankings.</p>
          </div>
          <Link to="/methodology" data-testid="home-methodology-link" className="px-5 py-2.5 rounded-full bg-white border border-sky-200 text-sky-700 text-sm font-semibold hover:bg-sky-500 hover:text-white active:scale-95 transition-colors duration-200 shrink-0">
            Our methodology
          </Link>
        </div>
      </section>
    </div>
  );
}
